import React, { useEffect, useMemo, useState } from 'react';
import { apiDelete, apiGet, apiPost, apiPut } from '../../utils/api';
import { DATE_HELP, openPicker, parseDateText } from './dateText';
import './EmployeeMemoPanel.css';
import './EmployeeLeavePanel.css';

// 직원 휴직 관리: 메모처럼 달이 아니라 사람에게 붙는 목록이다(사유·시작일·종료일).
// 휴직이 어느 달의 기산 기간에 걸치면 그 달 대장의 근태 시작일·종료일(가운데 휴직은 결근 등 공제)에 반영된다.

const API = '/api/payroll';
const EMPTY = { reason: '', start: '', end: '' };

const same = (a, b) => (a.reason || '') === (b.reason || '') && (a.start || '') === (b.start || '') && (a.end || '') === (b.end || '');
const OP_LABEL = { added: '추가', changed: '수정', removed: '삭제' };
const spanText = (x) => `${x.reason || '(사유 없음)'} · ${x.start} ~ ${x.end || '계속'}`;
// 종료일이 늦은 것이 위로. 종료일이 없는(계속 휴직) 것이 가장 위
const recentFirst = (a, b) => (b.end || '9999').localeCompare(a.end || '9999') || (b.start || '').localeCompare(a.start || '');

// 날짜 칸: 표의 날짜 칸처럼 직접 적거나(260321 · 0321 · 21) 달력에서 고른다. 칸을 벗어나면 2026-03-21 모양으로 맞춘다
function DateInput({ value, onChange, base, title, onKeyDown }) {
  const settle = () => {
    const iso = parseDateText(value, base.year, base.month);
    if (iso && iso !== value) onChange(iso);
  };
  return (
    <span className="leave-date">
      <input type="text" value={value || ''} placeholder="날짜" title={`${title} · ${DATE_HELP}`} onChange={(e) => onChange(e.target.value)} onBlur={settle} onKeyDown={onKeyDown} />
      <button type="button" tabIndex={-1} title="달력에서 고르기" onClick={(e) => openPicker(e.currentTarget.nextSibling)}>▦</button>
      <input type="date" className="leave-picker" tabIndex={-1} value={parseDateText(value, base.year, base.month) || ''} onChange={(e) => onChange(e.target.value)} />
    </span>
  );
}

// 한 줄: 사유 · 시작일 · 종료일
function LeaveFields({ value, onChange, onEnter, autoFocus, base }) {
  const set = (key) => (v) => onChange({ ...value, [key]: v });
  const keys = (e) => { if (e.key === 'Enter' && onEnter) onEnter(); e.stopPropagation(); };
  return (
    <>
      <input className="leave-reason" type="text" value={value.reason || ''} placeholder="사유" autoFocus={autoFocus} onChange={(e) => set('reason')(e.target.value)} onKeyDown={keys} />
      <DateInput value={value.start} onChange={set('start')} base={base} title="휴직시작일" onKeyDown={keys} />
      <DateInput value={value.end} onChange={set('end')} base={base} title="휴직종료일 (비우면 계속 휴직)" onKeyDown={keys} />
    </>
  );
}

function EmployeeLeavePanel({ companyId, row, year, month, onChanged, onCommit, onClose }) {
  const [leaves, setLeaves] = useState(null);       // 저장된 목록
  const [history, setHistory] = useState([]);       // 변경 이력(지운 휴직 포함), 최근 것이 위
  const [drafts, setDrafts] = useState({});         // 고치는 중인 값 {휴직 id: {reason, start, end}}
  const [fresh, setFresh] = useState(EMPTY);        // 새로 넣을 휴직
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const employeeId = row.employee_id;
  const base = `${API}/companies/${companyId}/leaves`;

  useEffect(() => {
    let alive = true;
    setError('');
    // 직원 id 가 아직 없는 줄은 휴직도 없다(처음 넣을 때 id 가 붙는다)
    if (!employeeId) { setLeaves([]); setHistory([]); return () => { alive = false; }; }
    apiGet(`${base}/${employeeId}`).then((r) => { if (alive) { setLeaves(r.leaves); setHistory(r.history || []); } }).catch((e) => { if (alive) setError(e.message); });
    return () => { alive = false; };
  }, [base, employeeId]);

  const sorted = useMemo(() => [...(leaves || [])].sort(recentFirst), [leaves]);

  // 저장·삭제를 한 뒤: 목록을 바꾸고, 대장을 다시 계산해 오게 한다
  const run = async (work) => {
    if (busy) return;
    setBusy(true);
    setError('');
    try {
      const result = await work();
      if (result && result.leaves) { setLeaves(result.leaves); setHistory(result.history || []); }
      await onChanged();
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  };
  // 적은 날짜를 읽는다. 읽을 수 없으면 알리고 null
  const base0 = { year, month };
  const dates = (v) => {
    const start = parseDateText(v.start, year, month);
    const end = String(v.end || '').trim() ? parseDateText(v.end, year, month) : '';
    if (!start) { setError(`휴직시작일을 ${DATE_HELP}`); return null; }
    if (end === null) { setError(`휴직종료일을 ${DATE_HELP}`); return null; }
    return { reason: v.reason || '', start, end: end || null };
  };
  const dropDraft = (id) => setDrafts((prev) => { const next = { ...prev }; delete next[id]; return next; });

  const add = () => {
    const body = dates(fresh);
    if (!body) return;
    run(async () => {
      const result = await apiPost(base, { row_id: row.id, ...body });
      setFresh(EMPTY);
      return result;
    });
  };
  const save = (leave) => {
    const body = dates(drafts[leave.id]);
    if (!body) return;
    run(async () => {
      const result = await apiPut(`${base}/${leave.id}`, body);
      dropDraft(leave.id);
      return result;
    });
  };
  const remove = (leave) => {
    if (!window.confirm(`${leave.reason || '휴직'} ${leave.start} ~ ${leave.end || '계속'} 을(를) 지울까요?\n이 기간에 걸친 달의 대장이 다시 계산됩니다.`)) return;
    run(() => apiDelete(`${base}/${leave.id}`));
  };

  // 목록이 생기기 전에 이 달 대장 줄에 직접 적어 둔 휴직(아직 목록으로 옮기지 않은 것)
  const old = row.values.leave_start
    ? { reason: row.values.leave_type || '', start: row.values.leave_start, end: row.values.leave_end || '' }
    : null;
  const clearOld = () => onCommit(['leave_type', 'leave_start', 'leave_end']
    .filter((key) => row.values[key] != null)
    .map((key) => ({ row_id: row.id, key, value: null })));
  const moveOld = () => run(async () => {
    const result = await apiPost(base, { row_id: row.id, reason: old.reason, start: old.start, end: old.end || null });
    await clearOld();
    return result;
  });

  const now = row.leaves_now || [];
  const applies = (leave) => now.some((x) => x.start === leave.start && (x.end || null) === (leave.end || null));

  return (
    <aside className="memo-panel leave-panel">
      <header className="memo-head leave-head">
        <div className="memo-who">
          <b>{row.values.name || '(이름 없음)'}</b>
          <span>휴직 관리</span>
          {row.values.id_number && <span>{String(row.values.id_number).slice(0, 6)}</span>}
        </div>
        <button type="button" className="memo-icon" onClick={onClose} title="닫기">✕</button>
      </header>

      <section className="leave-add">
        <div className="leave-cols"><span>사유</span><span>시작일</span><span>종료일</span><span /></div>
        <div className="leave-row">
          <LeaveFields value={fresh} onChange={setFresh} onEnter={add} autoFocus base={base0} />
          <button type="button" className="memo-btn primary leave-wide" disabled={busy || !String(fresh.start || '').trim()} onClick={add} title="휴직 추가 (Enter)">+ 추가</button>
        </div>
        {error && <div className="memo-error">⚠ {error}</div>}
      </section>

      <section className="leave-list">
        <h4>휴직 목록 <span>{leaves ? `${leaves.length + (old ? 1 : 0)}건 · 최근 것이 위` : '불러오는 중…'}</span></h4>
        {leaves && leaves.length === 0 && !old && <p className="memo-empty">등록된 휴직이 없습니다.</p>}
        {old && (
          <div className="leave-row old" title="예전 방식으로 이 달 대장 줄에만 적어 둔 휴직입니다. 목록으로 옮기면 다른 달에도 적용됩니다">
            <span className="leave-old-text">{old.reason || '(사유 없음)'} · {old.start} ~ {old.end || '계속'} <em>이 달에만 적힘</em></span>
            <button type="button" className="memo-btn primary leave-wide" disabled={busy} onClick={moveOld}>목록으로</button>
          </div>
        )}
        {sorted.map((leave) => {
          const draft = drafts[leave.id] || leave;
          const dirty = !same(draft, leave);
          return (
            <div key={leave.id} className={`leave-row ${applies(leave) ? 'now' : ''}`} title={applies(leave) ? '이 달 대장에 적용되는 휴직' : undefined}>
              <LeaveFields value={draft} onChange={(v) => setDrafts((prev) => ({ ...prev, [leave.id]: v }))} onEnter={dirty ? () => save(leave) : null} base={base0} />
              <button type="button" className="memo-btn primary" disabled={!dirty || busy} onClick={() => save(leave)} title="고친 내용 저장 (Enter)">저장</button>
              <button type="button" className="memo-btn danger" disabled={busy} onClick={() => remove(leave)} title="이 휴직 삭제">✕</button>
            </div>
          );
        })}
        <p className="leave-note">파란 띠는 이 달 대장에 적용되는 휴직입니다. 기산 기간의 앞뒤에 걸치면 근태 시작일·종료일이 당겨지고, 가운데 휴직은 결근 등 공제 일수가 됩니다. 종료일을 비우면 계속 휴직입니다. 날짜는 260321, 0321(그 해), 21(그 달)처럼 줄여 적어도 됩니다.</p>
        <details className="leave-history">
          <summary>변경 이력 <span>{history.length}건</span></summary>
          {history.length === 0 && <p className="memo-empty">아직 변경 이력이 없습니다.</p>}
          <ol>
            {history.map((h, i) => (
              <li key={`${h.leave_id}-${h.at}-${i}`} className={h.op}>
                <div className="leave-history-head">
                  <em>{OP_LABEL[h.op] || h.op}</em>
                  <span>{new Date(h.at).toLocaleString('ko-KR', { dateStyle: 'medium', timeStyle: 'short' })}</span>
                  <span>{h.by_name || h.by}</span>
                </div>
                <p>{spanText(h)}</p>
                {h.before && <p className="was">고치기 전: {spanText(h.before)}</p>}
              </li>
            ))}
          </ol>
        </details>
      </section>
    </aside>
  );
}

export default EmployeeLeavePanel;
